import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useProducts } from "@/context/ProductsContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Trash2, ArrowUp, ArrowDown, Edit3 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { id: null, label: "", kind: "url", value: "/", order: 0, is_active: true };

export default function AdminNav() {
  const { user } = useAuth();
  const { refresh } = useSite();
  const { categories } = useProducts();
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const r = await api.get("/admin/nav"); setList(r.data); } catch (e) { toast.error(formatErr(e)); }
    setLoading(false);
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/nav" replace />;
  if (user.role !== "admin") return <div className="pt-40 container-lux"><h1 className="font-serif text-4xl">Admin access required</h1></div>;

  const openNew = () => setEditing({ ...EMPTY, order: list.length });
  const openEdit = (n) => setEditing({ ...EMPTY, ...n });
  const close = () => setEditing(null);
  const set = (k, v) => setEditing({ ...editing, [k]: v });

  const save = async () => {
    if (!editing.label) return toast.error("Label is required");
    if (!editing.value) return toast.error("Link value is required");
    setSaving(true);
    const payload = {
      label: editing.label.trim(),
      kind: editing.kind,
      value: editing.value,
      order: Number(editing.order) || 0,
      is_active: editing.is_active !== false,
    };
    try {
      if (editing.id) await api.put(`/admin/nav/${editing.id}`, payload);
      else await api.post("/admin/nav", payload);
      toast.success("Nav item saved");
      close();
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
    setSaving(false);
  };

  const del = async () => {
    if (!editing.id) return;
    if (!confirm(`Delete "${editing.label}"?`)) return;
    try { await api.delete(`/admin/nav/${editing.id}`); toast("Deleted"); close(); await Promise.all([load(), refresh()]); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    try {
      await Promise.all([
        api.put(`/admin/nav/${a.id}`, { ...a, order: j }),
        api.put(`/admin/nav/${b.id}`, { ...b, order: idx }),
      ]);
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const linkPreview = (n) => n.kind === "category" ? `/category/${n.value}` : n.value;

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-nav-page">
      <div className="container-lux">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="overline text-muted-foreground">Atelier · Admin</p>
            <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-6xl font-light">Navigation</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">Rename, reorder, hide or add nav items. Each link points to a URL or a live category — pick from the dropdown.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/site" className="lux-btn-outline py-3 px-5 text-[10px]">Site Settings</Link>
            <button onClick={openNew} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="new-nav-btn"><Plus size={12} strokeWidth={1} /> New Nav Item</button>
          </div>
        </div>

        <div className="mt-12 border border-border">
          <div className="grid grid-cols-[60px_1fr_120px_1fr_100px_100px] gap-4 px-6 py-4 border-b border-border bg-sand/40 overline text-muted-foreground">
            <span>Order</span><span>Label</span><span>Kind</span><span>Link</span><span>Status</span><span className="text-right pr-2">Actions</span>
          </div>
          {loading ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">Loading…</p>
          ) : list.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">No nav items yet.</p>
          ) : list.map((n, idx) => (
            <div key={n.id} className="grid grid-cols-[60px_1fr_120px_1fr_100px_100px] gap-4 px-6 py-4 items-center border-b border-border/60 hover:bg-sand/20 transition-colors" data-testid={`nav-row-${n.id}`}>
              <div className="flex items-center gap-1">
                <span className="text-xs">{idx}</span>
                <div className="flex flex-col">
                  <button onClick={()=>move(idx, -1)} disabled={idx===0} className="p-0.5 disabled:opacity-30"><ArrowUp size={10} strokeWidth={1} /></button>
                  <button onClick={()=>move(idx, 1)} disabled={idx===list.length-1} className="p-0.5 disabled:opacity-30"><ArrowDown size={10} strokeWidth={1} /></button>
                </div>
              </div>
              <p className="font-serif text-lg">{n.label}</p>
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{n.kind}</span>
              <span className="text-xs text-muted-foreground truncate">{linkPreview(n)}</span>
              <span className={`text-[10px] uppercase tracking-widest ${n.is_active ? "text-ink" : "text-muted-foreground"}`}>{n.is_active ? "Live" : "Hidden"}</span>
              <div className="flex justify-end">
                <button onClick={()=>openEdit(n)} className="p-2 hover:bg-muted" title="Edit" data-testid={`edit-nav-${n.id}`}><Edit3 size={14} strokeWidth={1} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v)=>!v && close()}>
        <DialogContent className="max-w-2xl bg-ivory border border-border rounded-none p-0">
          <div className="p-8 border-b border-border flex items-center justify-between">
            <div>
              <p className="overline text-muted-foreground">{editing?.id ? "Editing" : "New Nav Item"}</p>
              <DialogHeader><DialogTitle className="font-serif text-3xl tracking-tighter font-light mt-2">{editing?.label || "Untitled"}</DialogTitle></DialogHeader>
            </div>
            <div className="flex gap-2">
              {editing?.id && <button onClick={del} className="lux-btn-outline py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="del-nav"><Trash2 size={12} strokeWidth={1} /> Delete</button>}
              <button onClick={close} className="lux-btn-outline py-3 px-5 text-[10px]">Close</button>
              <button onClick={save} disabled={saving} className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="save-nav"><Save size={12} strokeWidth={1} /> {saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
          {editing && (
          <div className="p-8 space-y-6">
            <div>
              <label className="overline text-muted-foreground">Label</label>
              <input value={editing.label} onChange={(e)=>set("label", e.target.value)} className="lux-input" data-testid="n-label" placeholder="Bridal" />
            </div>
            <div>
              <label className="overline text-muted-foreground">Link Kind</label>
              <div className="flex gap-2 mt-3">
                {[["url","URL / Path"],["category","Category"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={()=>set("kind", v)} data-testid={`n-kind-${v}`}
                    className={`px-4 py-2 text-[11px] uppercase tracking-widest border ${editing.kind === v ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="overline text-muted-foreground">{editing.kind === "category" ? "Category" : "URL / Path"}</label>
              {editing.kind === "category" ? (
                <select value={editing.value} onChange={(e)=>set("value", e.target.value)} className="lux-input" data-testid="n-value">
                  <option value="">— pick a category —</option>
                  {categories.map(c => <option key={c.slug} value={c.slug}>{c.name} · /category/{c.slug}</option>)}
                </select>
              ) : (
                <input value={editing.value} onChange={(e)=>set("value", e.target.value)} placeholder="/collections/bridal or https://…" className="lux-input" data-testid="n-value" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="overline text-muted-foreground">Order</label>
                <input type="number" value={editing.order} onChange={(e)=>set("order", e.target.value)} className="lux-input" data-testid="n-order" />
              </div>
              <label className="flex items-end gap-3 text-sm pb-2">
                <input type="checkbox" checked={editing.is_active !== false} onChange={(e)=>set("is_active", e.target.checked)} data-testid="n-active" />
                Live in the header
              </label>
            </div>
          </div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
